// Lightweight i18n for HACCP Manager — Fase A (critical pre-payment screens + emails)
// Languages: Italian (default), English, Arabic, Bengali, Albanian, Romanian

export const LANGUAGES = [
    { code: "it", label: "Italiano", flag: "🇮🇹" },
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "ar", label: "العربية", flag: "🇪🇬", rtl: true },
    { code: "bn", label: "বাংলা", flag: "🇧🇩" },
    { code: "sq", label: "Shqip", flag: "🇦🇱" },
    { code: "ro", label: "Română", flag: "🇷🇴" },
];

const STORAGE_KEY = "haccp_lang";

export function getStoredLang() {
    if (typeof window === "undefined") return "it";
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANGUAGES.find((l) => l.code === stored)) return stored;
    const browser = (navigator.language || "it").slice(0, 2);
    if (LANGUAGES.find((l) => l.code === browser)) return browser;
    return "it";
}

export function setStoredLang(code) {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, code);
    // Apply RTL/LTR direction to <html>
    const lang = LANGUAGES.find((l) => l.code === code);
    document.documentElement.dir = lang?.rtl ? "rtl" : "ltr";
    document.documentElement.lang = code;
}

// Translation dictionaries
// Only critical pre-payment screens + onboarding presets are translated for Fase A.
// Rest of the app stays in Italian (clients understand technical HACCP terms).
const DICT = {
    it: {
        // Brand
        "brand.tagline": "Autocontrollo · Spese · Scadenze",

        // Landing
        "landing.cta_trial_badge": "7 giorni gratis · Nessuna carta",
        "landing.hero_title_1": "Il tuo registro HACCP",
        "landing.hero_title_2": "sempre in regola",
        "landing.hero_subtitle": "HACCP Manager è l'app italiana per gestire registro HACCP, temperature frigoriferi, fatture con AI e scadenze fiscali. Pensata per il commercio alimentare.",
        "landing.cta_start_free": "Inizia prova gratuita",
        "landing.cta_login": "Hai già un account? Accedi",
        "landing.compliance_note": "✓ Conforme Reg. CE 852/2004 · ✓ Privacy GDPR · ✓ Disdici quando vuoi",
        "landing.benefits_title": "Risparmia tempo, sii in regola, dormi sereno",
        "landing.benefit_time_title": "Risparmi ore ogni settimana",
        "landing.benefit_time_desc": "Niente più carte, raccoglitori, calcoli a mano.",
        "landing.benefit_legal_title": "Sempre in regola",
        "landing.benefit_legal_desc": "Conforme Reg. CE 852/2004. Pronto per controlli ASL/NAS.",
        "landing.benefit_cloud_title": "Backup automatico",
        "landing.benefit_cloud_desc": "I tuoi dati al sicuro nel cloud, accessibili ovunque.",
        "landing.features_title": "Tutto quello che ti serve",
        "landing.features_subtitle": "Un'unica app per HACCP, fatture, scadenze, documenti e report.",
        "landing.feature_haccp_title": "Registro HACCP digitale",
        "landing.feature_haccp_desc": "Checklist giornaliere personalizzate sul tuo tipo di attività.",
        "landing.feature_temp_title": "Controllo temperature",
        "landing.feature_temp_desc": "Frigoriferi e celle frigo sotto controllo, con grafici e notifiche.",
        "landing.feature_ocr_title": "Fatture con AI OCR",
        "landing.feature_ocr_desc": "Fotografa una fattura e l'AI estrae fornitore, importo, IVA.",
        "landing.feature_deadlines_title": "Scadenze e documenti",
        "landing.feature_deadlines_desc": "F24, IVA, INPS, autorizzazioni: notifiche prima della scadenza.",
        "landing.feature_pdf_title": "Report PDF per ASL",
        "landing.feature_pdf_desc": "Genera report professionali in un tocco.",
        "landing.feature_pwa_title": "App PWA",
        "landing.feature_pwa_desc": "Funziona su PC, smartphone e tablet, anche offline.",
        "landing.targets_title": "Pensata per chi vende cibo",
        "landing.targets_subtitle": "Tutte le attività soggette al manuale HACCP.",
        "landing.target_ortofrutta": "Negozi di frutta e verdura",
        "landing.target_alimentari": "Alimentari e mini market",
        "landing.target_macelleria": "Macellerie e pescherie",
        "landing.target_panificio": "Panifici e pasticcerie",
        "landing.target_ristorante": "Bar, ristoranti, pizzerie",
        "landing.target_gastronomia": "Gastronomie e rosticcerie",
        "landing.pricing_title": "Un prezzo. Tutto incluso.",
        "landing.pricing_subtitle": "Nessun extra, nessuna sorpresa, disdici quando vuoi.",
        "landing.pricing_plan_label": "Piano Mensile",
        "landing.pricing_period": "€/mese",
        "landing.pricing_trial_note": "7 giorni di prova gratuita",
        "landing.pricing_feat_1": "Registro HACCP digitale completo",
        "landing.pricing_feat_2": "Frigoriferi e temperature illimitati",
        "landing.pricing_feat_3": "AI OCR fatture illimitato",
        "landing.pricing_feat_4": "Scadenze, documenti, calendario",
        "landing.pricing_feat_5": "Report PDF per ASL e NAS",
        "landing.pricing_feat_6": "Notifiche push (anche offline)",
        "landing.pricing_feat_7": "Aggiornamenti automatici",
        "landing.pricing_feat_8": "Supporto via email",
        "landing.pricing_cta": "Inizia prova gratuita",
        "landing.pricing_disclaimer": "Nessuna carta richiesta · Pagamento sicuro con Stripe",
        "landing.final_cta_title": "Pronto a digitalizzare il tuo negozio?",
        "landing.final_cta_subtitle": "Inizia oggi. 7 giorni gratis, senza carta.",
        "landing.final_cta_btn": "Crea account gratis",
        "landing.header_login": "Accedi",
        "landing.header_register": "Inizia gratis",

        // Auth common
        "auth.email": "Email",
        "auth.password": "Password",
        "auth.name": "Nome",
        "auth.forgot_password": "Password dimenticata?",
        "auth.create_account": "Crea account",
        "auth.have_account": "Hai già un account?",
        "auth.login_link": "Accedi",

        // Login
        "login.title": "Accedi",
        "login.subtitle": "Bentornato! Inserisci le tue credenziali.",
        "login.submit": "Accedi",
        "login.submitting": "Accesso in corso...",
        "login.email_placeholder": "la-tua-email@esempio.it",
        "login.footer_note": "Hai un negozio alimentare? Prova HACCP Manager gratis per 7 giorni · 9,99€/mese · Disdici quando vuoi",
        "login.toast_success": "Accesso effettuato",

        // Register
        "register.badge": "7 giorni gratis · Nessuna carta richiesta",
        "register.title": "Crea il tuo account",
        "register.subtitle": "Inizia a gestire HACCP, spese e scadenze del tuo negozio.",
        "register.password_hint": "Password (min. 6 caratteri)",
        "register.submit": "Inizia prova gratuita",
        "register.submitting": "Creazione account...",
        "register.password_too_short": "La password deve contenere almeno 6 caratteri",
        "register.disclaimer": "Iscrivendoti accetti le condizioni d'uso · Disdici in qualsiasi momento",
        "register.toast_success": "Account creato · Prova gratuita 7 giorni attiva",
        "register.name_placeholder": "Mario Rossi",
        "register.email_placeholder": "email@esempio.it",

        // Forgot / Reset
        "forgot.back": "Torna all'accesso",
        "forgot.title": "Password dimenticata?",
        "forgot.subtitle": "Inserisci la tua email: ti invieremo un link per scegliere una nuova password.",
        "forgot.submit": "Invia link di recupero",
        "forgot.submitting": "Invio in corso...",
        "forgot.success": "Se l'email è registrata, riceverai a breve un link per reimpostare la password. Controlla anche la cartella spam/promozioni.",
        "reset.title": "Nuova password",
        "reset.subtitle": "Scegli una password sicura per il tuo account.",
        "reset.hint": "Nuova password (min. 6 caratteri)",
        "reset.submit": "Salva nuova password",
        "reset.invalid_token": "Link non valido o scaduto.",
        "reset.request_new": "Richiedi nuovo link",
        "reset.toast_success": "Password reimpostata · Ora accedi",

        // Billing
        "billing.title": "Abbonamento",
        "billing.subtitle": "Gestisci il tuo piano HACCP Manager.",
        "billing.current_plan": "Piano attuale",
        "billing.status": "Stato",
        "billing.status_active": "Attivo",
        "billing.status_inactive": "Inattivo",
        "billing.plan_monthly": "Mensile 9,99€",
        "billing.plan_trial": "Prova gratuita",
        "billing.plan_canceled": "Annullato",
        "billing.plan_expired": "Scaduto",
        "billing.day": "giorno",
        "billing.days": "giorni",
        "billing.upgrade_title": "Passa al piano Mensile",
        "billing.upgrade_subtitle": "Continua a usare tutte le funzionalità: HACCP, fatture OCR, scadenze, PDF, notifiche push.",
        "billing.upgrade_feat_1": "Tutto incluso",
        "billing.upgrade_feat_2": "Frigoriferi illimitati",
        "billing.upgrade_feat_3": "OCR fatture illimitato",
        "billing.upgrade_feat_4": "Disdici quando vuoi",
        "billing.subscribe_now": "Sottoscrivi ora",
        "billing.opening_stripe": "Apertura Stripe...",
        "billing.secure_note": "Pagamento sicuro tramite Stripe · Nessun vincolo · Annulla in qualsiasi momento",
        "billing.cancel_btn": "Annulla abbonamento",
        "billing.cancel_confirm": "Vuoi davvero annullare l'abbonamento?",
        "billing.cancel_success": "Abbonamento annullato",
        "billing.admin_note": "Sei admin: hai accesso completo senza abbonamento.",

        // Trial banner
        "trial.days_remaining": "Prova gratuita: {days} {unit} rimasti",
        "trial.day_remaining": "Prova gratuita: 1 giorno rimasto",
        "trial.upgrade_link": "· Passa a 9,99€/mese →",

        // Lang switcher
        "lang.title": "Lingua",
    },

    en: {
        "brand.tagline": "HACCP · Expenses · Deadlines",
        "landing.cta_trial_badge": "7 days free · No credit card",
        "landing.hero_title_1": "Your HACCP register",
        "landing.hero_title_2": "always compliant",
        "landing.hero_subtitle": "HACCP Manager is the Italian app to manage HACCP register, fridge temperatures, AI-powered invoices and tax deadlines. Made for food businesses.",
        "landing.cta_start_free": "Start free trial",
        "landing.cta_login": "Already have an account? Sign in",
        "landing.compliance_note": "✓ EU Reg. 852/2004 compliant · ✓ GDPR Privacy · ✓ Cancel anytime",
        "landing.benefits_title": "Save time, stay compliant, sleep well",
        "landing.benefit_time_title": "Save hours every week",
        "landing.benefit_time_desc": "No more paper, binders, manual calculations.",
        "landing.benefit_legal_title": "Always compliant",
        "landing.benefit_legal_desc": "Compliant with EU Reg. 852/2004. Ready for health inspections.",
        "landing.benefit_cloud_title": "Automatic backup",
        "landing.benefit_cloud_desc": "Your data safe in the cloud, accessible anywhere.",
        "landing.features_title": "Everything you need",
        "landing.features_subtitle": "One app for HACCP, invoices, deadlines, documents and reports.",
        "landing.feature_haccp_title": "Digital HACCP register",
        "landing.feature_haccp_desc": "Daily checklists customized for your business type.",
        "landing.feature_temp_title": "Temperature control",
        "landing.feature_temp_desc": "Fridges and cold rooms under control, with charts and alerts.",
        "landing.feature_ocr_title": "Invoices with AI OCR",
        "landing.feature_ocr_desc": "Snap a photo of an invoice — AI extracts supplier, amount, VAT.",
        "landing.feature_deadlines_title": "Deadlines and documents",
        "landing.feature_deadlines_desc": "Taxes, VAT, social security, permits: alerts before expiry.",
        "landing.feature_pdf_title": "PDF reports for inspections",
        "landing.feature_pdf_desc": "Generate professional reports in one tap.",
        "landing.feature_pwa_title": "PWA app",
        "landing.feature_pwa_desc": "Works on PC, smartphone and tablet, even offline.",
        "landing.targets_title": "Built for food businesses",
        "landing.targets_subtitle": "All businesses subject to the HACCP manual.",
        "landing.target_ortofrutta": "Fruit and vegetable shops",
        "landing.target_alimentari": "Groceries and mini markets",
        "landing.target_macelleria": "Butchers and fishmongers",
        "landing.target_panificio": "Bakeries and pastry shops",
        "landing.target_ristorante": "Bars, restaurants, pizzerias",
        "landing.target_gastronomia": "Delis and rotisseries",
        "landing.pricing_title": "One price. All included.",
        "landing.pricing_subtitle": "No extras, no surprises, cancel anytime.",
        "landing.pricing_plan_label": "Monthly Plan",
        "landing.pricing_period": "€/month",
        "landing.pricing_trial_note": "7 days free trial",
        "landing.pricing_feat_1": "Full digital HACCP register",
        "landing.pricing_feat_2": "Unlimited fridges and temperatures",
        "landing.pricing_feat_3": "Unlimited AI OCR invoices",
        "landing.pricing_feat_4": "Deadlines, documents, calendar",
        "landing.pricing_feat_5": "PDF reports for inspections",
        "landing.pricing_feat_6": "Push notifications (also offline)",
        "landing.pricing_feat_7": "Automatic updates",
        "landing.pricing_feat_8": "Email support",
        "landing.pricing_cta": "Start free trial",
        "landing.pricing_disclaimer": "No card required · Secure payment with Stripe",
        "landing.final_cta_title": "Ready to digitize your shop?",
        "landing.final_cta_subtitle": "Start today. 7 days free, no card.",
        "landing.final_cta_btn": "Create free account",
        "landing.header_login": "Sign in",
        "landing.header_register": "Start free",
        "auth.email": "Email",
        "auth.password": "Password",
        "auth.name": "Name",
        "auth.forgot_password": "Forgot password?",
        "auth.create_account": "Create account",
        "auth.have_account": "Already have an account?",
        "auth.login_link": "Sign in",
        "login.title": "Sign in",
        "login.subtitle": "Welcome back! Enter your credentials.",
        "login.submit": "Sign in",
        "login.submitting": "Signing in...",
        "login.email_placeholder": "your-email@example.com",
        "login.footer_note": "Run a food business? Try HACCP Manager free for 7 days · €9.99/month · Cancel anytime",
        "login.toast_success": "Signed in",
        "register.badge": "7 days free · No card required",
        "register.title": "Create your account",
        "register.subtitle": "Start managing HACCP, expenses and deadlines.",
        "register.password_hint": "Password (min. 6 characters)",
        "register.submit": "Start free trial",
        "register.submitting": "Creating account...",
        "register.password_too_short": "Password must be at least 6 characters",
        "register.disclaimer": "By signing up you accept the terms · Cancel anytime",
        "register.toast_success": "Account created · 7-day free trial active",
        "register.name_placeholder": "John Doe",
        "register.email_placeholder": "email@example.com",
        "forgot.back": "Back to sign in",
        "forgot.title": "Forgot password?",
        "forgot.subtitle": "Enter your email: we'll send you a link to choose a new password.",
        "forgot.submit": "Send recovery link",
        "forgot.submitting": "Sending...",
        "forgot.success": "If the email is registered, you'll soon receive a link to reset your password. Also check spam/promotions.",
        "reset.title": "New password",
        "reset.subtitle": "Choose a secure password for your account.",
        "reset.hint": "New password (min. 6 characters)",
        "reset.submit": "Save new password",
        "reset.invalid_token": "Invalid or expired link.",
        "reset.request_new": "Request new link",
        "reset.toast_success": "Password reset · Sign in now",
        "billing.title": "Subscription",
        "billing.subtitle": "Manage your HACCP Manager plan.",
        "billing.current_plan": "Current plan",
        "billing.status": "Status",
        "billing.status_active": "Active",
        "billing.status_inactive": "Inactive",
        "billing.plan_monthly": "Monthly €9.99",
        "billing.plan_trial": "Free trial",
        "billing.plan_canceled": "Canceled",
        "billing.plan_expired": "Expired",
        "billing.day": "day",
        "billing.days": "days",
        "billing.upgrade_title": "Upgrade to Monthly plan",
        "billing.upgrade_subtitle": "Keep using all features: HACCP, OCR invoices, deadlines, PDF, push notifications.",
        "billing.upgrade_feat_1": "All included",
        "billing.upgrade_feat_2": "Unlimited fridges",
        "billing.upgrade_feat_3": "Unlimited invoice OCR",
        "billing.upgrade_feat_4": "Cancel anytime",
        "billing.subscribe_now": "Subscribe now",
        "billing.opening_stripe": "Opening Stripe...",
        "billing.secure_note": "Secure payment via Stripe · No commitment · Cancel anytime",
        "billing.cancel_btn": "Cancel subscription",
        "billing.cancel_confirm": "Do you really want to cancel your subscription?",
        "billing.cancel_success": "Subscription canceled",
        "billing.admin_note": "You are admin: full access without subscription.",
        "trial.days_remaining": "Free trial: {days} {unit} left",
        "trial.day_remaining": "Free trial: 1 day left",
        "trial.upgrade_link": "· Upgrade to €9.99/month →",
        "lang.title": "Language",
    },

    ar: {
        "brand.tagline": "HACCP · النفقات · المواعيد النهائية",
        "landing.cta_trial_badge": "٧ أيام مجانية · بدون بطاقة",
        "landing.hero_title_1": "سجل HACCP الخاص بك",
        "landing.hero_title_2": "دائمًا متوافق",
        "landing.hero_subtitle": "HACCP Manager هو تطبيق إيطالي لإدارة سجل HACCP ودرجات حرارة الثلاجات والفواتير بالذكاء الاصطناعي والمواعيد الضريبية. مصمم للمحلات الغذائية.",
        "landing.cta_start_free": "ابدأ التجربة المجانية",
        "landing.cta_login": "لديك حساب؟ سجّل الدخول",
        "landing.compliance_note": "✓ متوافق مع لائحة الاتحاد الأوروبي 852/2004 · ✓ خصوصية GDPR · ✓ إلغاء في أي وقت",
        "landing.benefits_title": "وفر الوقت، التزم بالقانون، نم مرتاحًا",
        "landing.benefit_time_title": "وفّر ساعات كل أسبوع",
        "landing.benefit_time_desc": "لا مزيد من الأوراق والملفات والحسابات اليدوية.",
        "landing.benefit_legal_title": "دائمًا متوافق",
        "landing.benefit_legal_desc": "متوافق مع لائحة 852/2004. جاهز للتفتيش الصحي.",
        "landing.benefit_cloud_title": "نسخ احتياطي تلقائي",
        "landing.benefit_cloud_desc": "بياناتك آمنة في السحابة، متاحة من أي مكان.",
        "landing.features_title": "كل ما تحتاجه",
        "landing.features_subtitle": "تطبيق واحد لـ HACCP والفواتير والمواعيد والوثائق والتقارير.",
        "landing.feature_haccp_title": "سجل HACCP رقمي",
        "landing.feature_haccp_desc": "قوائم تحقق يومية مخصصة لنوع نشاطك.",
        "landing.feature_temp_title": "مراقبة درجة الحرارة",
        "landing.feature_temp_desc": "الثلاجات والغرف الباردة تحت السيطرة، مع الرسوم البيانية والتنبيهات.",
        "landing.feature_ocr_title": "فواتير بالذكاء الاصطناعي",
        "landing.feature_ocr_desc": "صور الفاتورة والذكاء الاصطناعي يستخرج المورد والمبلغ وضريبة القيمة المضافة.",
        "landing.feature_deadlines_title": "مواعيد ووثائق",
        "landing.feature_deadlines_desc": "الضرائب، الضمان الاجتماعي، التراخيص: تنبيهات قبل الانتهاء.",
        "landing.feature_pdf_title": "تقارير PDF للتفتيش",
        "landing.feature_pdf_desc": "إنشاء تقارير احترافية بضغطة واحدة.",
        "landing.feature_pwa_title": "تطبيق PWA",
        "landing.feature_pwa_desc": "يعمل على الكمبيوتر والهاتف والجهاز اللوحي، حتى دون اتصال.",
        "landing.targets_title": "مصمم لتجار الطعام",
        "landing.targets_subtitle": "جميع الأنشطة الخاضعة لدليل HACCP.",
        "landing.target_ortofrutta": "محلات الخضار والفاكهة",
        "landing.target_alimentari": "البقالة والسوبر ماركت الصغير",
        "landing.target_macelleria": "اللحوم والأسماك",
        "landing.target_panificio": "المخابز والحلويات",
        "landing.target_ristorante": "المقاهي والمطاعم والبيتزا",
        "landing.target_gastronomia": "محلات الأطعمة الجاهزة",
        "landing.pricing_title": "سعر واحد. كل شيء مشمول.",
        "landing.pricing_subtitle": "بدون إضافات، بدون مفاجآت، ألغِ في أي وقت.",
        "landing.pricing_plan_label": "الخطة الشهرية",
        "landing.pricing_period": "€/شهر",
        "landing.pricing_trial_note": "٧ أيام تجربة مجانية",
        "landing.pricing_feat_1": "سجل HACCP رقمي كامل",
        "landing.pricing_feat_2": "ثلاجات ودرجات حرارة بلا حدود",
        "landing.pricing_feat_3": "OCR فواتير بلا حدود",
        "landing.pricing_feat_4": "مواعيد، وثائق، تقويم",
        "landing.pricing_feat_5": "تقارير PDF للتفتيش",
        "landing.pricing_feat_6": "إشعارات Push (حتى دون اتصال)",
        "landing.pricing_feat_7": "تحديثات تلقائية",
        "landing.pricing_feat_8": "دعم عبر البريد الإلكتروني",
        "landing.pricing_cta": "ابدأ التجربة المجانية",
        "landing.pricing_disclaimer": "لا حاجة لبطاقة · دفع آمن عبر Stripe",
        "landing.final_cta_title": "هل أنت مستعد لرقمنة محلك؟",
        "landing.final_cta_subtitle": "ابدأ اليوم. ٧ أيام مجانية، بدون بطاقة.",
        "landing.final_cta_btn": "أنشئ حسابًا مجانيًا",
        "landing.header_login": "تسجيل الدخول",
        "landing.header_register": "ابدأ مجانًا",
        "auth.email": "البريد الإلكتروني",
        "auth.password": "كلمة المرور",
        "auth.name": "الاسم",
        "auth.forgot_password": "نسيت كلمة المرور؟",
        "auth.create_account": "أنشئ حسابًا",
        "auth.have_account": "لديك حساب بالفعل؟",
        "auth.login_link": "تسجيل الدخول",
        "login.title": "تسجيل الدخول",
        "login.subtitle": "مرحبًا بعودتك! أدخل بيانات الاعتماد.",
        "login.submit": "تسجيل الدخول",
        "login.submitting": "جارٍ تسجيل الدخول...",
        "login.email_placeholder": "your-email@example.com",
        "login.footer_note": "تدير محلًا غذائيًا؟ جرّب HACCP Manager مجانًا لمدة ٧ أيام · ٩,٩٩€/شهر · ألغِ في أي وقت",
        "login.toast_success": "تم تسجيل الدخول",
        "register.badge": "٧ أيام مجانية · بدون بطاقة",
        "register.title": "أنشئ حسابك",
        "register.subtitle": "ابدأ في إدارة HACCP والنفقات والمواعيد.",
        "register.password_hint": "كلمة المرور (٦ أحرف على الأقل)",
        "register.submit": "ابدأ التجربة المجانية",
        "register.submitting": "جارٍ إنشاء الحساب...",
        "register.password_too_short": "يجب أن تحتوي كلمة المرور على ٦ أحرف على الأقل",
        "register.disclaimer": "بالتسجيل، فإنك توافق على الشروط · ألغِ في أي وقت",
        "register.toast_success": "تم إنشاء الحساب · التجربة المجانية ٧ أيام نشطة",
        "register.name_placeholder": "محمد علي",
        "register.email_placeholder": "email@example.com",
        "forgot.back": "العودة إلى تسجيل الدخول",
        "forgot.title": "نسيت كلمة المرور؟",
        "forgot.subtitle": "أدخل بريدك الإلكتروني: سنرسل لك رابطًا لاختيار كلمة مرور جديدة.",
        "forgot.submit": "إرسال رابط الاستعادة",
        "forgot.submitting": "جارٍ الإرسال...",
        "forgot.success": "إذا كان البريد مسجلًا، ستتلقى قريبًا رابطًا لإعادة تعيين كلمة المرور. تحقق أيضًا من البريد العشوائي/العروض.",
        "reset.title": "كلمة مرور جديدة",
        "reset.subtitle": "اختر كلمة مرور آمنة لحسابك.",
        "reset.hint": "كلمة مرور جديدة (٦ أحرف على الأقل)",
        "reset.submit": "حفظ كلمة المرور الجديدة",
        "reset.invalid_token": "رابط غير صالح أو منتهي الصلاحية.",
        "reset.request_new": "طلب رابط جديد",
        "reset.toast_success": "تمت إعادة تعيين كلمة المرور · سجّل الدخول الآن",
        "billing.title": "الاشتراك",
        "billing.subtitle": "أدر خطة HACCP Manager الخاصة بك.",
        "billing.current_plan": "الخطة الحالية",
        "billing.status": "الحالة",
        "billing.status_active": "نشط",
        "billing.status_inactive": "غير نشط",
        "billing.plan_monthly": "شهري ٩,٩٩€",
        "billing.plan_trial": "تجربة مجانية",
        "billing.plan_canceled": "ملغى",
        "billing.plan_expired": "منتهي",
        "billing.day": "يوم",
        "billing.days": "أيام",
        "billing.upgrade_title": "الترقية إلى الخطة الشهرية",
        "billing.upgrade_subtitle": "استمر في استخدام جميع الميزات: HACCP، فواتير OCR، مواعيد، PDF، إشعارات push.",
        "billing.upgrade_feat_1": "كل شيء مشمول",
        "billing.upgrade_feat_2": "ثلاجات بلا حدود",
        "billing.upgrade_feat_3": "OCR فواتير بلا حدود",
        "billing.upgrade_feat_4": "ألغِ في أي وقت",
        "billing.subscribe_now": "اشترك الآن",
        "billing.opening_stripe": "جارٍ فتح Stripe...",
        "billing.secure_note": "دفع آمن عبر Stripe · بدون التزام · ألغِ في أي وقت",
        "billing.cancel_btn": "إلغاء الاشتراك",
        "billing.cancel_confirm": "هل تريد فعلاً إلغاء اشتراكك؟",
        "billing.cancel_success": "تم إلغاء الاشتراك",
        "billing.admin_note": "أنت مشرف: وصول كامل بدون اشتراك.",
        "trial.days_remaining": "التجربة المجانية: متبقي {days} {unit}",
        "trial.day_remaining": "التجربة المجانية: يوم واحد متبقي",
        "trial.upgrade_link": "· الترقية إلى ٩,٩٩€/شهر ←",
        "lang.title": "اللغة",
    },

    bn: {
        "brand.tagline": "HACCP · খরচ · সময়সীমা",
        "landing.cta_trial_badge": "৭ দিন ফ্রি · কোনো কার্ড নেই",
        "landing.hero_title_1": "আপনার HACCP রেজিস্টার",
        "landing.hero_title_2": "সবসময় নিয়ম মেনে",
        "landing.hero_subtitle": "HACCP Manager হল ইতালিয়ান অ্যাপ যা HACCP রেজিস্টার, ফ্রিজের তাপমাত্রা, AI দিয়ে চালান এবং কর সময়সীমা পরিচালনা করে। খাদ্য ব্যবসার জন্য তৈরি।",
        "landing.cta_start_free": "ফ্রি ট্রায়াল শুরু করুন",
        "landing.cta_login": "ইতিমধ্যে অ্যাকাউন্ট আছে? সাইন ইন",
        "landing.compliance_note": "✓ EU Reg. 852/2004 অনুযায়ী · ✓ GDPR গোপনীয়তা · ✓ যেকোনো সময় বাতিল",
        "landing.benefits_title": "সময় বাঁচান, নিয়ম মানুন, শান্তিতে ঘুমান",
        "landing.benefit_time_title": "প্রতি সপ্তাহে ঘণ্টা সাশ্রয়",
        "landing.benefit_time_desc": "আর কাগজ, ফাইল, হাতে হিসাব নয়।",
        "landing.benefit_legal_title": "সবসময় নিয়ম মেনে",
        "landing.benefit_legal_desc": "EU Reg. 852/2004 অনুযায়ী। স্বাস্থ্য পরিদর্শনের জন্য প্রস্তুত।",
        "landing.benefit_cloud_title": "স্বয়ংক্রিয় ব্যাকআপ",
        "landing.benefit_cloud_desc": "ক্লাউডে নিরাপদ ডেটা, যেকোনো জায়গা থেকে অ্যাক্সেসযোগ্য।",
        "landing.features_title": "আপনার যা প্রয়োজন সবই",
        "landing.features_subtitle": "HACCP, চালান, সময়সীমা, ডকুমেন্ট এবং রিপোর্টের জন্য একটি অ্যাপ।",
        "landing.feature_haccp_title": "ডিজিটাল HACCP রেজিস্টার",
        "landing.feature_haccp_desc": "আপনার ব্যবসার ধরন অনুযায়ী দৈনিক চেকলিস্ট।",
        "landing.feature_temp_title": "তাপমাত্রা নিয়ন্ত্রণ",
        "landing.feature_temp_desc": "ফ্রিজ এবং কোল্ড রুম নিয়ন্ত্রণে, চার্ট এবং অ্যালার্ট সহ।",
        "landing.feature_ocr_title": "AI OCR সহ চালান",
        "landing.feature_ocr_desc": "চালানের ছবি তুলুন — AI সরবরাহকারী, পরিমাণ, VAT বের করবে।",
        "landing.feature_deadlines_title": "সময়সীমা এবং ডকুমেন্ট",
        "landing.feature_deadlines_desc": "কর, VAT, সামাজিক নিরাপত্তা, অনুমতি: মেয়াদের আগে অ্যালার্ট।",
        "landing.feature_pdf_title": "পরিদর্শনের জন্য PDF রিপোর্ট",
        "landing.feature_pdf_desc": "এক ট্যাপে পেশাদার রিপোর্ট তৈরি করুন।",
        "landing.feature_pwa_title": "PWA অ্যাপ",
        "landing.feature_pwa_desc": "PC, স্মার্টফোন, ট্যাবলেটে কাজ করে, অফলাইনেও।",
        "landing.targets_title": "খাদ্য ব্যবসায়ীদের জন্য তৈরি",
        "landing.targets_subtitle": "HACCP ম্যানুয়ালের অধীনে সমস্ত ব্যবসা।",
        "landing.target_ortofrutta": "ফল ও সবজির দোকান",
        "landing.target_alimentari": "মুদি ও মিনি মার্কেট",
        "landing.target_macelleria": "মাংস ও মাছের দোকান",
        "landing.target_panificio": "বেকারি ও পেস্ট্রি",
        "landing.target_ristorante": "বার, রেস্তোরাঁ, পিৎজা",
        "landing.target_gastronomia": "ডেলি ও রোস্টিসেরি",
        "landing.pricing_title": "এক দাম। সব অন্তর্ভুক্ত।",
        "landing.pricing_subtitle": "কোনো অতিরিক্ত নেই, কোনো চমক নেই, যেকোনো সময় বাতিল।",
        "landing.pricing_plan_label": "মাসিক প্ল্যান",
        "landing.pricing_period": "€/মাস",
        "landing.pricing_trial_note": "৭ দিন ফ্রি ট্রায়াল",
        "landing.pricing_feat_1": "সম্পূর্ণ ডিজিটাল HACCP রেজিস্টার",
        "landing.pricing_feat_2": "সীমাহীন ফ্রিজ ও তাপমাত্রা",
        "landing.pricing_feat_3": "সীমাহীন AI OCR চালান",
        "landing.pricing_feat_4": "সময়সীমা, ডকুমেন্ট, ক্যালেন্ডার",
        "landing.pricing_feat_5": "পরিদর্শনের জন্য PDF রিপোর্ট",
        "landing.pricing_feat_6": "Push নোটিফিকেশন (অফলাইনেও)",
        "landing.pricing_feat_7": "স্বয়ংক্রিয় আপডেট",
        "landing.pricing_feat_8": "ইমেইল সহায়তা",
        "landing.pricing_cta": "ফ্রি ট্রায়াল শুরু করুন",
        "landing.pricing_disclaimer": "কোনো কার্ড লাগবে না · Stripe দিয়ে নিরাপদ পেমেন্ট",
        "landing.final_cta_title": "আপনার দোকান ডিজিটাল করতে প্রস্তুত?",
        "landing.final_cta_subtitle": "আজই শুরু করুন। ৭ দিন ফ্রি, কোনো কার্ড নেই।",
        "landing.final_cta_btn": "ফ্রি অ্যাকাউন্ট তৈরি করুন",
        "landing.header_login": "সাইন ইন",
        "landing.header_register": "ফ্রি শুরু করুন",
        "auth.email": "ইমেইল",
        "auth.password": "পাসওয়ার্ড",
        "auth.name": "নাম",
        "auth.forgot_password": "পাসওয়ার্ড ভুলে গেছেন?",
        "auth.create_account": "অ্যাকাউন্ট তৈরি",
        "auth.have_account": "ইতিমধ্যে অ্যাকাউন্ট আছে?",
        "auth.login_link": "সাইন ইন",
        "login.title": "সাইন ইন",
        "login.subtitle": "স্বাগতম! আপনার শংসাপত্র লিখুন।",
        "login.submit": "সাইন ইন",
        "login.submitting": "সাইন ইন হচ্ছে...",
        "login.email_placeholder": "your-email@example.com",
        "login.footer_note": "খাদ্য ব্যবসা চালান? HACCP Manager ৭ দিন ফ্রি ব্যবহার করুন · €৯.৯৯/মাস · যেকোনো সময় বাতিল",
        "login.toast_success": "সাইন ইন সফল",
        "register.badge": "৭ দিন ফ্রি · কোনো কার্ড লাগবে না",
        "register.title": "আপনার অ্যাকাউন্ট তৈরি করুন",
        "register.subtitle": "HACCP, খরচ ও সময়সীমা পরিচালনা শুরু করুন।",
        "register.password_hint": "পাসওয়ার্ড (ন্যূনতম ৬ অক্ষর)",
        "register.submit": "ফ্রি ট্রায়াল শুরু করুন",
        "register.submitting": "অ্যাকাউন্ট তৈরি হচ্ছে...",
        "register.password_too_short": "পাসওয়ার্ড কমপক্ষে ৬ অক্ষর হতে হবে",
        "register.disclaimer": "সাইন আপ করে আপনি শর্তাবলী মেনে নিচ্ছেন · যেকোনো সময় বাতিল",
        "register.toast_success": "অ্যাকাউন্ট তৈরি হয়েছে · ৭ দিনের ফ্রি ট্রায়াল সক্রিয়",
        "register.name_placeholder": "Mohammad Ali",
        "register.email_placeholder": "email@example.com",
        "forgot.back": "সাইন ইনে ফিরে যান",
        "forgot.title": "পাসওয়ার্ড ভুলে গেছেন?",
        "forgot.subtitle": "আপনার ইমেইল লিখুন: আমরা একটি নতুন পাসওয়ার্ড লিঙ্ক পাঠাব।",
        "forgot.submit": "রিকভারি লিঙ্ক পাঠান",
        "forgot.submitting": "পাঠানো হচ্ছে...",
        "forgot.success": "যদি ইমেইল নিবন্ধিত থাকে, আপনি শীঘ্রই পাসওয়ার্ড রিসেট লিঙ্ক পাবেন। স্প্যাম/প্রোমোশনও দেখুন।",
        "reset.title": "নতুন পাসওয়ার্ড",
        "reset.subtitle": "আপনার অ্যাকাউন্টের জন্য একটি নিরাপদ পাসওয়ার্ড নির্বাচন করুন।",
        "reset.hint": "নতুন পাসওয়ার্ড (ন্যূনতম ৬ অক্ষর)",
        "reset.submit": "নতুন পাসওয়ার্ড সংরক্ষণ",
        "reset.invalid_token": "অবৈধ বা মেয়াদ উত্তীর্ণ লিঙ্ক।",
        "reset.request_new": "নতুন লিঙ্ক অনুরোধ করুন",
        "reset.toast_success": "পাসওয়ার্ড রিসেট · এখন সাইন ইন করুন",
        "billing.title": "সাবস্ক্রিপশন",
        "billing.subtitle": "আপনার HACCP Manager প্ল্যান পরিচালনা করুন।",
        "billing.current_plan": "বর্তমান প্ল্যান",
        "billing.status": "অবস্থা",
        "billing.status_active": "সক্রিয়",
        "billing.status_inactive": "নিষ্ক্রিয়",
        "billing.plan_monthly": "মাসিক €৯.৯৯",
        "billing.plan_trial": "ফ্রি ট্রায়াল",
        "billing.plan_canceled": "বাতিল",
        "billing.plan_expired": "মেয়াদ উত্তীর্ণ",
        "billing.day": "দিন",
        "billing.days": "দিন",
        "billing.upgrade_title": "মাসিক প্ল্যানে আপগ্রেড করুন",
        "billing.upgrade_subtitle": "সমস্ত বৈশিষ্ট্য ব্যবহার চালিয়ে যান।",
        "billing.upgrade_feat_1": "সব অন্তর্ভুক্ত",
        "billing.upgrade_feat_2": "সীমাহীন ফ্রিজ",
        "billing.upgrade_feat_3": "সীমাহীন চালান OCR",
        "billing.upgrade_feat_4": "যেকোনো সময় বাতিল",
        "billing.subscribe_now": "এখন সাবস্ক্রাইব করুন",
        "billing.opening_stripe": "Stripe খোলা হচ্ছে...",
        "billing.secure_note": "Stripe দিয়ে নিরাপদ পেমেন্ট · কোনো প্রতিশ্রুতি নেই · যেকোনো সময় বাতিল",
        "billing.cancel_btn": "সাবস্ক্রিপশন বাতিল",
        "billing.cancel_confirm": "আপনি কি সত্যিই সাবস্ক্রিপশন বাতিল করতে চান?",
        "billing.cancel_success": "সাবস্ক্রিপশন বাতিল করা হয়েছে",
        "billing.admin_note": "আপনি অ্যাডমিন: সাবস্ক্রিপশন ছাড়াই পূর্ণ অ্যাক্সেস।",
        "trial.days_remaining": "ফ্রি ট্রায়াল: {days} {unit} বাকি",
        "trial.day_remaining": "ফ্রি ট্রায়াল: ১ দিন বাকি",
        "trial.upgrade_link": "· €৯.৯৯/মাসে আপগ্রেড →",
        "lang.title": "ভাষা",
    },

    sq: {
        "brand.tagline": "HACCP · Shpenzimet · Afatet",
        "landing.cta_trial_badge": "7 ditë falas · Pa kartë",
        "landing.hero_title_1": "Regjistri juaj HACCP",
        "landing.hero_title_2": "gjithmonë në rregull",
        "landing.hero_subtitle": "HACCP Manager është aplikacioni italian për menaxhimin e regjistrit HACCP, temperaturat e frigoriferëve, faturat me AI dhe afatet fiskale. I bërë për biznese ushqimore.",
        "landing.cta_start_free": "Fillo provën falas",
        "landing.cta_login": "Ke llogari? Hyr",
        "landing.compliance_note": "✓ Në përputhje me Reg. KE 852/2004 · ✓ Privatësia GDPR · ✓ Anulo kur të duash",
        "landing.benefits_title": "Kurse kohë, qëndro në rregull, fli i qetë",
        "landing.benefit_time_title": "Kursen orë çdo javë",
        "landing.benefit_time_desc": "Pa letra, dosje, llogaritje me dorë.",
        "landing.benefit_legal_title": "Gjithmonë në rregull",
        "landing.benefit_legal_desc": "Në përputhje me Reg. 852/2004. Gati për kontroll shëndetësor.",
        "landing.benefit_cloud_title": "Backup automatik",
        "landing.benefit_cloud_desc": "Të dhënat e tua të sigurta në cloud, të arritshme kudo.",
        "landing.features_title": "Gjithçka që të nevojitet",
        "landing.features_subtitle": "Një aplikacion për HACCP, fatura, afate, dokumente dhe raporte.",
        "landing.feature_haccp_title": "Regjistër HACCP digjital",
        "landing.feature_haccp_desc": "Lista kontrolli ditore të personalizuara.",
        "landing.feature_temp_title": "Kontroll i temperaturës",
        "landing.feature_temp_desc": "Frigoriferë dhe dhoma të ftohta nën kontroll, me grafika dhe njoftime.",
        "landing.feature_ocr_title": "Fatura me AI OCR",
        "landing.feature_ocr_desc": "Fotografo një faturë — AI nxjerr furnizuesin, shumën, TVSH.",
        "landing.feature_deadlines_title": "Afate dhe dokumente",
        "landing.feature_deadlines_desc": "Taksa, TVSH, sigurime, leje: njoftime para skadimit.",
        "landing.feature_pdf_title": "Raporte PDF për kontroll",
        "landing.feature_pdf_desc": "Gjenero raporte profesionale me një prekje.",
        "landing.feature_pwa_title": "Aplikacion PWA",
        "landing.feature_pwa_desc": "Funksionon në PC, telefon dhe tablet, edhe offline.",
        "landing.targets_title": "Ndërtuar për ata që shesin ushqim",
        "landing.targets_subtitle": "Të gjitha bizneset që i nënshtrohen manualit HACCP.",
        "landing.target_ortofrutta": "Dyqane fruta dhe perime",
        "landing.target_alimentari": "Ushqimore dhe mini-markete",
        "landing.target_macelleria": "Mishërore dhe peshkore",
        "landing.target_panificio": "Furra dhe ëmbëltore",
        "landing.target_ristorante": "Bare, restorante, picari",
        "landing.target_gastronomia": "Gastronomi dhe rosticeri",
        "landing.pricing_title": "Një çmim. Gjithçka brenda.",
        "landing.pricing_subtitle": "Pa shtesa, pa surpriza, anulo kur të duash.",
        "landing.pricing_plan_label": "Plani Mujor",
        "landing.pricing_period": "€/muaj",
        "landing.pricing_trial_note": "7 ditë provë falas",
        "landing.pricing_feat_1": "Regjistër HACCP digjital i plotë",
        "landing.pricing_feat_2": "Frigoriferë dhe temperatura të pakufizuara",
        "landing.pricing_feat_3": "OCR fatura të pakufizuar",
        "landing.pricing_feat_4": "Afate, dokumente, kalendar",
        "landing.pricing_feat_5": "Raporte PDF për kontroll",
        "landing.pricing_feat_6": "Njoftime push (edhe offline)",
        "landing.pricing_feat_7": "Përditësime automatike",
        "landing.pricing_feat_8": "Mbështetje me email",
        "landing.pricing_cta": "Fillo provën falas",
        "landing.pricing_disclaimer": "Pa kartë · Pagesë e sigurt me Stripe",
        "landing.final_cta_title": "Gati ta digjitalizosh dyqanin?",
        "landing.final_cta_subtitle": "Fillo sot. 7 ditë falas, pa kartë.",
        "landing.final_cta_btn": "Krijo llogari falas",
        "landing.header_login": "Hyr",
        "landing.header_register": "Fillo falas",
        "auth.email": "Email",
        "auth.password": "Fjalëkalimi",
        "auth.name": "Emri",
        "auth.forgot_password": "Harruat fjalëkalimin?",
        "auth.create_account": "Krijo llogari",
        "auth.have_account": "Ke llogari?",
        "auth.login_link": "Hyr",
        "login.title": "Hyr",
        "login.subtitle": "Mirë se erdhe! Fut kredencialet.",
        "login.submit": "Hyr",
        "login.submitting": "Po hyn...",
        "login.email_placeholder": "your-email@example.com",
        "login.footer_note": "Ke biznes ushqimor? Provo HACCP Manager 7 ditë falas · 9,99€/muaj · Anulo kur të duash",
        "login.toast_success": "Hyrja u krye",
        "register.badge": "7 ditë falas · Pa kartë",
        "register.title": "Krijo llogarinë tënde",
        "register.subtitle": "Fillo të menaxhosh HACCP, shpenzime dhe afate.",
        "register.password_hint": "Fjalëkalim (min. 6 karaktere)",
        "register.submit": "Fillo provën falas",
        "register.submitting": "Po krijohet llogaria...",
        "register.password_too_short": "Fjalëkalimi duhet të ketë të paktën 6 karaktere",
        "register.disclaimer": "Duke u regjistruar pranon kushtet · Anulo kur të duash",
        "register.toast_success": "Llogaria u krijua · Prova 7 ditore aktive",
        "register.name_placeholder": "Edon Krasniqi",
        "register.email_placeholder": "email@example.com",
        "forgot.back": "Kthehu te hyrja",
        "forgot.title": "Harruat fjalëkalimin?",
        "forgot.subtitle": "Fut email-in: do të dërgojmë një link për fjalëkalim të ri.",
        "forgot.submit": "Dërgo linkun e rikuperimit",
        "forgot.submitting": "Po dërgohet...",
        "forgot.success": "Nëse email-i është i regjistruar, do të marrësh shpejt linkun. Kontrollo edhe spam/promo.",
        "reset.title": "Fjalëkalim i ri",
        "reset.subtitle": "Zgjidh një fjalëkalim të sigurt.",
        "reset.hint": "Fjalëkalim i ri (min. 6 karaktere)",
        "reset.submit": "Ruaj fjalëkalimin e ri",
        "reset.invalid_token": "Lidhje e pavlefshme ose e skaduar.",
        "reset.request_new": "Kërko link të ri",
        "reset.toast_success": "Fjalëkalimi u rivendos · Hyr tani",
        "billing.title": "Abonimi",
        "billing.subtitle": "Menaxho planin tënd HACCP Manager.",
        "billing.current_plan": "Plani aktual",
        "billing.status": "Statusi",
        "billing.status_active": "Aktiv",
        "billing.status_inactive": "Joaktiv",
        "billing.plan_monthly": "Mujor 9,99€",
        "billing.plan_trial": "Provë falas",
        "billing.plan_canceled": "Anuluar",
        "billing.plan_expired": "Skaduar",
        "billing.day": "ditë",
        "billing.days": "ditë",
        "billing.upgrade_title": "Kalo në planin Mujor",
        "billing.upgrade_subtitle": "Vazhdo të përdorësh të gjitha veçoritë.",
        "billing.upgrade_feat_1": "Gjithçka brenda",
        "billing.upgrade_feat_2": "Frigoriferë të pakufizuar",
        "billing.upgrade_feat_3": "OCR fatura të pakufizuar",
        "billing.upgrade_feat_4": "Anulo kur të duash",
        "billing.subscribe_now": "Abonohu tani",
        "billing.opening_stripe": "Po hapet Stripe...",
        "billing.secure_note": "Pagesë e sigurt me Stripe · Pa angazhim · Anulo kur të duash",
        "billing.cancel_btn": "Anulo abonimin",
        "billing.cancel_confirm": "Vërtet do ta anulosh abonimin?",
        "billing.cancel_success": "Abonimi u anulua",
        "billing.admin_note": "Je admin: qasje e plotë pa abonim.",
        "trial.days_remaining": "Provë falas: {days} {unit} të mbetura",
        "trial.day_remaining": "Provë falas: 1 ditë e mbetur",
        "trial.upgrade_link": "· Kalo në 9,99€/muaj →",
        "lang.title": "Gjuha",
    },

    ro: {
        "brand.tagline": "HACCP · Cheltuieli · Termene",
        "landing.cta_trial_badge": "7 zile gratis · Fără card",
        "landing.hero_title_1": "Registrul tău HACCP",
        "landing.hero_title_2": "mereu în regulă",
        "landing.hero_subtitle": "HACCP Manager este aplicația italiană pentru gestionarea registrului HACCP, temperaturilor frigiderelor, facturilor cu AI și termenelor fiscale. Pentru afaceri alimentare.",
        "landing.cta_start_free": "Începe perioada gratuită",
        "landing.cta_login": "Ai cont? Conectează-te",
        "landing.compliance_note": "✓ Conform Reg. CE 852/2004 · ✓ Confidențialitate GDPR · ✓ Anulează oricând",
        "landing.benefits_title": "Economisește timp, fii în regulă, dormi liniștit",
        "landing.benefit_time_title": "Economisești ore săptămânal",
        "landing.benefit_time_desc": "Fără hârtii, dosare, calcule manuale.",
        "landing.benefit_legal_title": "Mereu în regulă",
        "landing.benefit_legal_desc": "Conform Reg. 852/2004. Gata pentru controale sanitare.",
        "landing.benefit_cloud_title": "Backup automat",
        "landing.benefit_cloud_desc": "Datele tale în siguranță în cloud, accesibile oriunde.",
        "landing.features_title": "Tot ce ai nevoie",
        "landing.features_subtitle": "O aplicație pentru HACCP, facturi, termene, documente și rapoarte.",
        "landing.feature_haccp_title": "Registru HACCP digital",
        "landing.feature_haccp_desc": "Liste de verificare zilnice personalizate.",
        "landing.feature_temp_title": "Control temperaturi",
        "landing.feature_temp_desc": "Frigidere și camere reci sub control, cu grafice și alerte.",
        "landing.feature_ocr_title": "Facturi cu AI OCR",
        "landing.feature_ocr_desc": "Fotografiază o factură — AI extrage furnizor, sumă, TVA.",
        "landing.feature_deadlines_title": "Termene și documente",
        "landing.feature_deadlines_desc": "Taxe, TVA, contribuții, autorizații: alerte înainte de scadență.",
        "landing.feature_pdf_title": "Rapoarte PDF pentru control",
        "landing.feature_pdf_desc": "Generează rapoarte profesionale cu un singur tap.",
        "landing.feature_pwa_title": "Aplicație PWA",
        "landing.feature_pwa_desc": "Funcționează pe PC, telefon și tabletă, chiar și offline.",
        "landing.targets_title": "Pentru cei care vând mâncare",
        "landing.targets_subtitle": "Toate afacerile supuse manualului HACCP.",
        "landing.target_ortofrutta": "Magazine fructe și legume",
        "landing.target_alimentari": "Alimentare și mini-marketuri",
        "landing.target_macelleria": "Măcelării și pescării",
        "landing.target_panificio": "Brutării și cofetării",
        "landing.target_ristorante": "Baruri, restaurante, pizzerii",
        "landing.target_gastronomia": "Gastronomii și rotiserii",
        "landing.pricing_title": "Un preț. Tot inclus.",
        "landing.pricing_subtitle": "Fără extra, fără surprize, anulează oricând.",
        "landing.pricing_plan_label": "Plan Lunar",
        "landing.pricing_period": "€/lună",
        "landing.pricing_trial_note": "7 zile probă gratuită",
        "landing.pricing_feat_1": "Registru HACCP digital complet",
        "landing.pricing_feat_2": "Frigidere și temperaturi nelimitate",
        "landing.pricing_feat_3": "OCR facturi nelimitat",
        "landing.pricing_feat_4": "Termene, documente, calendar",
        "landing.pricing_feat_5": "Rapoarte PDF pentru control",
        "landing.pricing_feat_6": "Notificări push (și offline)",
        "landing.pricing_feat_7": "Actualizări automate",
        "landing.pricing_feat_8": "Suport prin email",
        "landing.pricing_cta": "Începe perioada gratuită",
        "landing.pricing_disclaimer": "Fără card · Plată sigură cu Stripe",
        "landing.final_cta_title": "Gata să-ți digitalizezi magazinul?",
        "landing.final_cta_subtitle": "Începe azi. 7 zile gratis, fără card.",
        "landing.final_cta_btn": "Creează cont gratuit",
        "landing.header_login": "Conectează-te",
        "landing.header_register": "Începe gratis",
        "auth.email": "Email",
        "auth.password": "Parolă",
        "auth.name": "Nume",
        "auth.forgot_password": "Ai uitat parola?",
        "auth.create_account": "Creează cont",
        "auth.have_account": "Ai deja cont?",
        "auth.login_link": "Conectează-te",
        "login.title": "Conectare",
        "login.subtitle": "Bun venit! Introdu credențialele.",
        "login.submit": "Conectează-te",
        "login.submitting": "Se conectează...",
        "login.email_placeholder": "your-email@example.com",
        "login.footer_note": "Ai o afacere alimentară? Încearcă HACCP Manager 7 zile gratis · 9,99€/lună · Anulează oricând",
        "login.toast_success": "Conectare reușită",
        "register.badge": "7 zile gratis · Fără card",
        "register.title": "Creează-ți contul",
        "register.subtitle": "Începe să gestionezi HACCP, cheltuieli și termene.",
        "register.password_hint": "Parolă (min. 6 caractere)",
        "register.submit": "Începe perioada gratuită",
        "register.submitting": "Se creează contul...",
        "register.password_too_short": "Parola trebuie să aibă cel puțin 6 caractere",
        "register.disclaimer": "Prin înregistrare accepți condițiile · Anulează oricând",
        "register.toast_success": "Cont creat · Probă 7 zile activă",
        "register.name_placeholder": "Ion Popescu",
        "register.email_placeholder": "email@example.com",
        "forgot.back": "Înapoi la conectare",
        "forgot.title": "Ai uitat parola?",
        "forgot.subtitle": "Introdu email-ul: îți trimitem un link pentru o parolă nouă.",
        "forgot.submit": "Trimite link de recuperare",
        "forgot.submitting": "Se trimite...",
        "forgot.success": "Dacă email-ul este înregistrat, vei primi în curând un link. Verifică și spam/promoții.",
        "reset.title": "Parolă nouă",
        "reset.subtitle": "Alege o parolă sigură.",
        "reset.hint": "Parolă nouă (min. 6 caractere)",
        "reset.submit": "Salvează parola nouă",
        "reset.invalid_token": "Link invalid sau expirat.",
        "reset.request_new": "Cere link nou",
        "reset.toast_success": "Parola resetată · Conectează-te acum",
        "billing.title": "Abonament",
        "billing.subtitle": "Gestionează planul tău HACCP Manager.",
        "billing.current_plan": "Planul actual",
        "billing.status": "Status",
        "billing.status_active": "Activ",
        "billing.status_inactive": "Inactiv",
        "billing.plan_monthly": "Lunar 9,99€",
        "billing.plan_trial": "Probă gratuită",
        "billing.plan_canceled": "Anulat",
        "billing.plan_expired": "Expirat",
        "billing.day": "zi",
        "billing.days": "zile",
        "billing.upgrade_title": "Treci la planul Lunar",
        "billing.upgrade_subtitle": "Continuă să folosești toate funcțiile.",
        "billing.upgrade_feat_1": "Tot inclus",
        "billing.upgrade_feat_2": "Frigidere nelimitate",
        "billing.upgrade_feat_3": "OCR facturi nelimitat",
        "billing.upgrade_feat_4": "Anulează oricând",
        "billing.subscribe_now": "Abonează-te acum",
        "billing.opening_stripe": "Se deschide Stripe...",
        "billing.secure_note": "Plată sigură cu Stripe · Fără angajament · Anulează oricând",
        "billing.cancel_btn": "Anulează abonamentul",
        "billing.cancel_confirm": "Sigur vrei să anulezi abonamentul?",
        "billing.cancel_success": "Abonament anulat",
        "billing.admin_note": "Ești admin: acces complet fără abonament.",
        "trial.days_remaining": "Probă gratuită: {days} {unit} rămase",
        "trial.day_remaining": "Probă gratuită: 1 zi rămasă",
        "trial.upgrade_link": "· Treci la 9,99€/lună →",
        "lang.title": "Limba",
    },
};

let currentLang = "it";
const listeners = new Set();

export function initLang() {
    currentLang = getStoredLang();
    const lang = LANGUAGES.find((l) => l.code === currentLang);
    if (typeof document !== "undefined") {
        document.documentElement.dir = lang?.rtl ? "rtl" : "ltr";
        document.documentElement.lang = currentLang;
    }
}

export function changeLang(code) {
    if (!DICT[code]) return;
    currentLang = code;
    setStoredLang(code);
    listeners.forEach((fn) => fn(code));
}

export function getCurrentLang() {
    return currentLang;
}

export function t(key, vars) {
    const dict = DICT[currentLang] || DICT.it;
    let str = dict[key] || DICT.it[key] || key;
    if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
            str = str.replace(`{${k}}`, v);
        });
    }
    return str;
}

// React hook
import { useEffect, useState } from "react";
export function useT() {
    const [, force] = useState(0);
    useEffect(() => {
        const fn = () => force((x) => x + 1);
        listeners.add(fn);
        return () => listeners.delete(fn);
    }, []);
    return { t, lang: currentLang, changeLang };
}
